import React from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Avatar } from '../../components/ui/avatar';
import { IconWithBackground } from '../../components/ui/icon-with-background';
import {
    FeatherX,
    FeatherClock,
    FeatherCheckCircle,
    FeatherAlertCircle,
    FeatherUser,
    FeatherMessageSquare
} from '../../components/ui/icons';
import './ConversationDetail.css';

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
};

const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
        case 'resolved':
            return 'success';
        case 'dismissed':
            return 'neutral';
        case 'in_progress':
        case 'waiting_for_time':
            return 'warning';
        case 'pending':
            return 'default';
        default:
            return 'default';
    }
};

const getTypeLabel = (type) => {
    switch (type) {
        case 'work_day_reminder':
            return 'Work Day Reminder';
        case 'task_blocker_analysis':
            return 'Task Blocker Analysis';
        case 'unified':
            return 'Unified Conversation';
        case 'work_day_closing':
            return 'Work Day Closing';
        default:
            return type;
    }
};

const ConversationDetail = ({ conversation, onClose }) => {
    if (!conversation) return null;

    const conversationThread = conversation.conversation_thread || [];

    return (
        <div className="conversation-detail-overlay" onClick={onClose}>
            <div className="conversation-detail-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="conversation-detail-header">
                    <div className="flex items-center gap-3">
                        <Avatar size="large">
                            {conversation.user_name.charAt(0)}
                        </Avatar>
                        <div className="flex flex-col">
                            <h2 className="conversation-detail-title">{conversation.user_name}</h2>
                            <p className="conversation-detail-subtitle">{conversation.user_email}</p>
                        </div>
                    </div>
                    <Button variant="neutral" icon={<FeatherX />} onClick={onClose}>
                        Close
                    </Button>
                </div>

                {/* Metadata Section */}
                <div className="conversation-detail-metadata">
                    <div className="metadata-grid">
                        <div className="metadata-item">
                            <span className="metadata-label">Status:</span>
                            <Badge variant={getStatusVariant(conversation.status)}>
                                {conversation.status}
                            </Badge>
                        </div>
                        <div className="metadata-item">
                            <span className="metadata-label">Type:</span>
                            <Badge variant="neutral">{getTypeLabel(conversation.conversation_type)}</Badge>
                        </div>
                        <div className="metadata-item">
                            <span className="metadata-label">Goal Achieved:</span>
                            {conversation.goal_achieved ? (
                                <Badge variant="success" icon={<FeatherCheckCircle />}>
                                    Yes
                                </Badge>
                            ) : (
                                <Badge variant="neutral">No</Badge>
                            )}
                        </div>
                        {conversation.task && (
                            <div className="metadata-item">
                                <span className="metadata-label">Task:</span>
                                <span className="metadata-value">
                                    {conversation.task.display_id}: {conversation.task.title}
                                </span>
                            </div>
                        )}
                        <div className="metadata-item">
                            <span className="metadata-label">Created:</span>
                            <span className="metadata-value">{formatDate(conversation.created_at)}</span>
                        </div>
                        <div className="metadata-item">
                            <span className="metadata-label">Updated:</span>
                            <span className="metadata-value">{formatDate(conversation.updated_at)}</span>
                        </div>
                    </div>

                    {/* Next Planned Action */}
                    {conversation.next_planned_action && (
                        <div className="next-action-card">
                            <div className="card-header">
                                <FeatherClock className="w-5 h-5" />
                                <h3>Next Planned Action</h3>
                            </div>
                            <div className="card-content">
                                <div className="action-title">{conversation.next_planned_action.action}</div>
                                {conversation.next_planned_action.action_plan && (
                                    <div className="action-plan">
                                        <strong>Action Plan:</strong> {conversation.next_planned_action.action_plan}
                                    </div>
                                )}
                                {!conversation.next_planned_action.action_plan && conversation.next_planned_action.description && (
                                    <div className="action-description">{conversation.next_planned_action.description}</div>
                                )}
                                {conversation.next_planned_action.has_followup && conversation.next_planned_action.follow_up_time && (
                                    <div className="action-schedule highlight">
                                        <FeatherClock className="w-4 h-4" />
                                        <div className="followup-info">
                                            <div className="followup-label">Next Follow-up Time:</div>
                                            <div className="followup-time">{formatDate(conversation.next_planned_action.follow_up_time)}</div>
                                            {conversation.next_planned_action.time_until_followup && (
                                                <div className="time-until">Time until follow-up: {conversation.next_planned_action.time_until_followup}</div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {conversation.next_planned_action.scheduled_at && !conversation.next_planned_action.has_followup && (
                                    <div className="action-schedule">
                                        <FeatherClock className="w-4 h-4" />
                                        <span>Scheduled: {formatDate(conversation.next_planned_action.scheduled_at)}</span>
                                    </div>
                                )}
                                {conversation.next_planned_action.reason && (
                                    <div className="action-reason">
                                        <span className="reason-label">Reason:</span>
                                        <span>{conversation.next_planned_action.reason}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Follow-up Information */}
                    {conversation.scheduled_follow_up_at && (
                        <div className="follow-up-card">
                            <div className="card-header">
                                <FeatherClock className="w-5 h-5" />
                                <h3>Scheduled Follow-up</h3>
                            </div>
                            <div className="card-content">
                                <div className="follow-up-time">
                                    {formatDate(conversation.scheduled_follow_up_at)}
                                </div>
                                {conversation.follow_up_reason && (
                                    <div className="follow-up-reason">
                                        <span className="reason-label">Reason:</span>
                                        <span>{conversation.follow_up_reason}</span>
                                    </div>
                                )}
                                {conversation.metadata?.follow_up_attempts_after_commitment > 0 && (
                                    <div className="follow-up-attempts">
                                        <span className="attempts-label">Follow-up Attempts:</span>
                                        <span>{conversation.metadata.follow_up_attempts_after_commitment}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Metadata Details */}
                    {conversation.metadata && (
                        <div className="metadata-details-card">
                            <div className="card-header">
                                <h3>Conversation Metadata</h3>
                            </div>
                            <div className="card-content">
                                {conversation.metadata.current_topic && (
                                    <div className="metadata-detail-item">
                                        <span className="detail-label">Current Topic:</span>
                                        <span>{conversation.metadata.current_topic}</span>
                                    </div>
                                )}
                                {conversation.metadata.topic_queue && conversation.metadata.topic_queue.length > 0 && (
                                    <div className="metadata-detail-item">
                                        <span className="detail-label">Topic Queue:</span>
                                        <span>{conversation.metadata.topic_queue.join(', ')}</span>
                                    </div>
                                )}
                                {conversation.metadata.committed_time && (
                                    <div className="metadata-detail-item">
                                        <span className="detail-label">Committed Time:</span>
                                        <span>{formatDate(conversation.metadata.committed_time)}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Conversation Thread */}
                <div className="conversation-thread-section">
                    <h3 className="section-title">Conversation Thread</h3>
                    <div className="conversation-thread">
                        {conversationThread.length === 0 ? (
                            <div className="empty-thread">
                                <p>No messages in conversation thread</p>
                            </div>
                        ) : (
                            conversationThread.map((message, index) => (
                                <div
                                    key={index}
                                    className={`thread-message ${message.role === 'bot' ? 'bot-message' : 'user-message'}`}
                                >
                                    <div className="message-header">
                                        {message.role === 'bot' ? (
                                            <>
                                                <FeatherMessageSquare className="w-4 h-4" />
                                                <span className="message-role">{process.env.REACT_APP_BOT_NAME || 'Peter Bot'}</span>
                                            </>
                                        ) : (
                                            <>
                                                <FeatherUser className="w-4 h-4" />
                                                <span className="message-role">{conversation.user_name}</span>
                                            </>
                                        )}
                                        <span className="message-time">
                                            {formatDate(message.timestamp)}
                                        </span>
                                    </div>
                                    <div className="message-content">{message.content}</div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* AI Analysis (if available) */}
                {conversation.ai_analysis && (
                    <div className="ai-analysis-section">
                        <h3 className="section-title">AI Analysis</h3>
                        <div className="ai-analysis-content">
                            {conversation.ai_analysis}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ConversationDetail;

